/**
 * DesktopShell — layout principal pour `≥ 1280px` : Sidebar + Header +
 * 3 panneaux redimensionnables.
 *
 * @remarks
 * Remplace l'ancien `MainLayout.tsx` (517 LOC). Ici ~50 LOC car la logique
 * est extraite dans des hooks dédiés (`use-current-context`,
 * `use-context-lifecycle`, `use-feature-stores-subscription`,
 * `use-feature-shortcuts`).
 *
 * **N'importe AUCUNE feature directement.** Le contrat plugin est étanche.
 */

import { useCallback, useMemo } from 'react';
import { useCommand } from '@/app/commands/command-bus';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { useLayoutStore } from '@/app/stores/use-layout-store';
import { useFeatureFlagsStore } from '@/app/stores/use-feature-flags-store';
import { useCurrentContext } from '@/app/router/use-current-context';
import { useContextLifecycle } from '@/app/router/use-context-lifecycle';
import { useFeatureStoresSubscription } from '@/app/router/use-feature-stores-subscription';
import { useFeatureShortcuts } from '@/app/router/use-feature-shortcuts';
import { useFocusOnRouteChange } from '@/app/router/use-focus-on-route-change';
import { useDocumentTitle } from '@/app/router/use-document-title';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { PanelGroup } from './PanelGroup';
import { SkipLink } from './SkipLink';
import type { AppContext } from '@/app/registry';

export function DesktopShell() {
  const context = useCurrentContext();

  const user = useAuthStore((s) => s.user);
  const flags = useFeatureFlagsStore((s) => s.flags);
  // Référence stable tant que `user` ou `flags` ne changent pas — sinon les
  // features qui consomment `appCtx` re-rendent à chaque render du shell.
  const appCtx = useMemo<AppContext>(() => ({ user, flags }), [user, flags]);

  // Hooks shell — agnostiques aux features (consomment uniquement le registry)
  useFeatureStoresSubscription();
  useFeatureShortcuts(context, appCtx);
  useContextLifecycle(context, appCtx);
  useFocusOnRouteChange(context);
  useDocumentTitle(context, appCtx);

  // Raccourcis Ctrl+B / Ctrl+Shift+B (`shell-manifest`) : ici les panneaux
  // suivent le layout store, comme les boutons du header.
  const toggleLeftDrawer = useLayoutStore((s) => s.toggleLeftDrawer);
  const toggleRightDrawer = useLayoutStore((s) => s.toggleRightDrawer);
  const handleTogglePanel = useCallback(
    (side: 'left' | 'right') => {
      if (side === 'left') toggleLeftDrawer();
      else toggleRightDrawer();
    },
    [toggleLeftDrawer, toggleRightDrawer],
  );
  useCommand('shell:toggle-panel', handleTogglePanel);

  return (
    <div className="bg-bg text-fg flex h-screen w-screen flex-col overflow-hidden">
      <SkipLink />
      <Header context={context} appCtx={appCtx} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar context={context} appCtx={appCtx} />
        <div className="flex-1 overflow-hidden">
          <PanelGroup context={context} appCtx={appCtx} />
        </div>
      </div>
    </div>
  );
}
